'use client';

import { produce } from 'immer';
import { FC, ReactNode, useEffect } from 'react';
import { shallow } from 'zustand/shallow';

import { CheckoutApiResponse } from 'src/data/CheckoutApi/CheckoutApi.types';
import { BaseCarData } from 'src/types/Car.types';

import { storeFactory } from './storeFactory';

interface CartStoreStateData {
  carData: BaseCarData | null;
  carLoading: boolean;
  apiCartData: CheckoutApiResponse | null;
  apiCartLoading: boolean;
  cartCarSku: string | null;
  cartExpiry: Date | null;
  cartExpired: boolean;
}

interface CartStoreStateFunctions {
  setApiCartDataLoading: () => void;
  setCarDataLoading: () => void;
  setApiCartData: (apiCartData: CheckoutApiResponse | null) => void;
  setCarData: (carData: BaseCarData | null) => void;
  /** Empty the cart data. To be used when user logs out for example */
  clearCartData: () => void;
}

const {
  store: cartStore,
  StoreProvider: CartStoreProvider,
  useCreateStore: useCreateCartStore,
} = storeFactory<CartStoreStateData, CartStoreStateFunctions>(
  () => ({
    carData: null,
    carLoading: false,
    apiCartData: null,
    apiCartLoading: false,
    cartCarSku: null,
    cartExpiry: null,
    cartExpired: false,
  }),
  (set, get) => ({
    setApiCartDataLoading: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.apiCartLoading = true;
          draft.cartExpired = false;
        }),
      );
    },
    setCarDataLoading: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.carLoading = true;
        }),
      );
    },
    setApiCartData: (apiCartData: CheckoutApiResponse | null) => {
      set((state) =>
        produce(state, (draft) => {
          draft.apiCartData = apiCartData;
          draft.apiCartLoading = false;
          // Set cart expired if needed
          const cartExpiry = apiCartData?.vehicle.expiry
            ? new Date(`${apiCartData.vehicle.expiry}Z`) // Add UTC formatting to the date
            : null;
          const now = new Date();
          draft.cartExpiry = cartExpiry;
          draft.cartExpired =
            apiCartData === null ||
            (apiCartData !== null && apiCartData.vehicle.sku === null) ||
            (cartExpiry !== null && cartExpiry.getTime() < now.getTime());

          if (apiCartData === null) {
            // Reset the car SKU if needed
            draft.cartCarSku = null;
          } else {
            draft.cartCarSku = apiCartData.vehicle.most_recently_added_sku;
          }

          // Reset cartData if change of SKU
          if (get().cartCarSku !== draft.cartCarSku) {
            draft.carData = null;
            draft.carLoading = true;
          }
        }),
      );
    },
    setCarData: (cartData: BaseCarData | null) => {
      set((state) =>
        produce(state, (draft) => {
          draft.carData = cartData;
          draft.carLoading = false;
        }),
      );
    },
    clearCartData: () => {
      set((state) =>
        produce(state, (draft) => {
          draft.carData = null;
          draft.carLoading = false;
          draft.apiCartData = null;
          draft.apiCartLoading = false;
          draft.cartCarSku = null;
          draft.cartExpiry = null;
          draft.cartExpired = false;
        }),
      );
    },
  }),
);

export { cartStore, CartStoreProvider, useCreateCartStore };

export const CartStore: FC<{ children: ReactNode }> = ({ children }) => {
  const createCartStore = useCreateCartStore({});

  return <CartStoreProvider createStore={createCartStore}>{children}</CartStoreProvider>;
};

export const CartContextSetCartData: FC<Pick<CartStoreStateData, 'apiCartData'>> = ({ apiCartData }) => {
  const { setApiCartData } = cartStore(
    (state) => ({
      setApiCartData: state.setApiCartData,
    }),
    shallow,
  );

  useEffect(() => {
    setApiCartData(apiCartData);
  }, [apiCartData]);

  return null;
};
